import React, { useState } from 'react';
import type { GearConfig, GearItem, CartageConfig } from '@/types/GearConfig';
import { GearItemRow } from './GearItemRow';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ConfigSection } from '@/components/config_forms/shared/ConfigSection';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { Package, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GearConfigFormProps {
  config: GearConfig;
  onChange: (updated: GearConfig) => void;
}

const CARTAGE_LABELS: Record<CartageConfig['method'], string> = {
  none: 'None',
  flat: 'Flat Fee',
  mileage: 'Per Mile',
};

export const GearConfigForm: React.FC<GearConfigFormProps> = ({
  config,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleAddItem = () => {
    const newItem: GearItem = {
      id: crypto.randomUUID(),
      name: '',
      daily_rate: 0,
    };
    onChange({ ...config, gearItems: [...config.gearItems, newItem] });
  };

  const handleItemChange = (index: number, updated: GearItem) => {
    const items = [...config.gearItems];
    items[index] = updated;
    onChange({ ...config, gearItems: items });
  };

  const handleItemDelete = (index: number) => {
    const items = config.gearItems.filter((_, i) => i !== index);
    // Also remove deleted item from any kits
    const deletedId = config.gearItems[index].id;
    const kits = config.gearKits.map((kit) => ({
      ...kit,
      item_ids: kit.item_ids.filter((id) => id !== deletedId),
    }));
    onChange({ ...config, gearItems: items, gearKits: kits });
  };

  const handleCartageMethodChange = (method: CartageConfig['method']) => {
    const cartage: CartageConfig = { method };
    if (method === 'flat') cartage.flat_rate = config.cartage.flat_rate ?? 0;
    if (method === 'mileage') cartage.mileage_rate = config.cartage.mileage_rate ?? 0;
    onChange({ ...config, cartage });
  };

  return (
    <ConfigSection
      title="Gear Inventory"
      description="Equipment items available to charge as rental fees"
      icon={Package}
      iconClassName="text-primary"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className="space-y-4">
        {/* Gear items list */}
        <div className="space-y-3">
          {config.gearItems.map((item, index) => (
            <GearItemRow
              key={item.id}
              item={item}
              onChange={(updated) => handleItemChange(index, updated)}
              onDelete={() => handleItemDelete(index)}
            />
          ))}

          <Button
            variant="secondary"
            size="sm"
            onClick={handleAddItem}
            className="w-full"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Item
          </Button>
        </div>

        {/* Cartage config */}
        <div className="space-y-3 pt-3 border-t border-border">
          <div className="flex items-center gap-3">
            <Label className="text-xs text-muted-foreground w-16 shrink-0">Cartage</Label>
            <Select
              value={config.cartage.method}
              onValueChange={(v) => handleCartageMethodChange(v as CartageConfig['method'])}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CARTAGE_LABELS) as CartageConfig['method'][]).map((m) => (
                  <SelectItem key={m} value={m} className="text-xs">
                    {CARTAGE_LABELS[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <AnimatedCollapse open={config.cartage.method === 'flat'}>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-muted-foreground w-16 shrink-0">Flat Rate</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">$</span>
                    <input
                      type="number"
                      value={config.cartage.flat_rate ?? ''}
                      onChange={(e) =>
                        onChange({
                          ...config,
                          cartage: { ...config.cartage, flat_rate: e.target.value ? parseFloat(e.target.value) : undefined },
                        })
                      }
                      placeholder="0.00"
                      className={cn(
                        "h-8 w-24 rounded border bg-surface-2 px-2 text-right text-xs font-mono",
                        "focus:outline-none focus:ring-1 focus:ring-primary"
                      )}
                      step={0.01}
                      min={0}
                    />
                  </div>
                </div>
          </AnimatedCollapse>

          <AnimatedCollapse open={config.cartage.method === 'mileage'}>
                <div className="flex items-center gap-3">
                  <Label className="text-xs text-muted-foreground w-16 shrink-0">Rate</Label>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-muted-foreground">$</span>
                    <input
                      type="number"
                      value={config.cartage.mileage_rate ?? ''}
                      onChange={(e) =>
                        onChange({
                          ...config,
                          cartage: { ...config.cartage, mileage_rate: e.target.value ? parseFloat(e.target.value) : undefined },
                        })
                      }
                      placeholder="0.00"
                      className={cn(
                        "h-8 w-24 rounded border bg-surface-2 px-2 text-right text-xs font-mono",
                        "focus:outline-none focus:ring-1 focus:ring-primary"
                      )}
                      step={0.01}
                      min={0}
                    />
                    <span className="text-xs text-muted-foreground">/mi</span>
                  </div>
                </div>
          </AnimatedCollapse>
        </div>

        {/* Week length */}
        <div className="flex items-center gap-3 pt-3 border-t border-border">
          <Label className="text-xs text-muted-foreground w-24 shrink-0">Week Length</Label>
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={config.weekLengthDays}
              onChange={(e) =>
                onChange({ ...config, weekLengthDays: e.target.value ? parseInt(e.target.value, 10) : 5 })
              }
              className={cn(
                "h-8 w-16 rounded border bg-surface-2 px-2 text-right text-xs font-mono",
                "focus:outline-none focus:ring-1 focus:ring-primary"
              )}
              min={1}
              max={7}
            />
            <span className="text-xs text-muted-foreground">days</span>
          </div>
        </div>
      </div>
    </ConfigSection>
  );
};
